import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export const PRESETS = [7, 30, 90];
export const MAX_DAYS = 90;
const FALLBACK_ZONES = [
  "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York", "America/Sao_Paulo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Africa/Lagos", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo", "Australia/Sydney",
];

function validZone(tz) {
  if (!tz) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

const browserZone = () => validZone(Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC";

function todayIn(tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export function addDays(ymd, n) {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(a, b) {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
}

const isDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "");

export function shortDay(ymd) {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function when(iso, tz) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { timeZone: tz, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function zoneOptions(current) {
  const all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : FALLBACK_ZONES;
  const set = new Set(["UTC", ...all]);
  set.add(current);
  return [...set];
}

/** startDate / endDate / tz kept in the URL, like the Channel3 reporting window. */
export function useRange() {
  const [params, setParams] = useSearchParams();
  const tz = validZone(params.get("tz")) || browserZone();
  const today = todayIn(tz);
  const endDate = isDay(params.get("endDate")) ? params.get("endDate") : today;
  const startDate = isDay(params.get("startDate")) ? params.get("startDate") : addDays(endDate, -30);

  useEffect(() => {
    if (params.get("tz") !== tz || params.get("startDate") !== startDate || params.get("endDate") !== endDate) {
      setParams({ tz, startDate, endDate }, { replace: true });
    }
  }, [params, setParams, tz, startDate, endDate]);

  const setRange = (start, end, zone = tz) => {
    let s = start;
    let e = end;
    if (s > e) [s, e] = [e, s];
    if (daysBetween(s, e) > MAX_DAYS) s = addDays(e, -MAX_DAYS);
    setParams({ tz: zone, startDate: s, endDate: e });
  };

  const setZone = (zone) => {
    const span = daysBetween(startDate, endDate);
    const end = endDate === today ? todayIn(zone) : endDate;
    setRange(addDays(end, -span), end, zone);
  };

  return { tz, today, startDate, endDate, setRange, setZone, params: { startDate, endDate, tz } };
}

export function RangeBar({ range }) {
  const { tz, today, startDate, endDate, setRange, setZone } = range;
  const zones = useMemo(() => zoneOptions(tz), [tz]);
  return (
    <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="dev-range">
      {PRESETS.map((n) => {
        const active = endDate === today && daysBetween(startDate, endDate) === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => setRange(addDays(today, -n), today)}
            className={`px-3 py-1.5 text-xs rounded-full border ${active ? "bg-aura-accent text-white border-aura-accent" : "border-aura-border text-aura-muted hover:text-aura-ink"}`}
            data-testid={`range-${n}`}
          >
            {n}D
          </button>
        );
      })}
      <input
        type="date"
        value={startDate}
        max={endDate}
        onChange={(e) => isDay(e.target.value) && setRange(e.target.value, endDate)}
        className="px-3 py-1.5 text-xs border border-aura-border bg-aura-surface [color-scheme:dark]"
        data-testid="range-start"
      />
      <span className="text-xs text-aura-muted">to</span>
      <input
        type="date"
        value={endDate}
        min={startDate}
        max={today}
        onChange={(e) => isDay(e.target.value) && setRange(startDate, e.target.value)}
        className="px-3 py-1.5 text-xs border border-aura-border bg-aura-surface [color-scheme:dark]"
        data-testid="range-end"
      />
      <select
        value={tz}
        onChange={(e) => setZone(e.target.value)}
        className="px-3 py-1.5 text-xs border border-aura-border bg-aura-surface max-w-[220px]"
        data-testid="range-tz"
      >
        {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
      </select>
    </div>
  );
}
